// src/components/dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import { 
  CreditCardIcon,
  DocumentArrowUpIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon  // ← Added logout icon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTransactions, TRANSACTION_STATUS } from '../../hooks/useTransactions';
import FileUpload from '../upload/FileUpload';
import TransactionHistory from './TransactionHistory';
import CreditPurchaseModal from '../payment/CreditPurchaseModal';
import RefreshBanner from '../common/RefreshBanner';
import { waitForWebhookProcessing } from '../../utils/paystack';
import { useNavigate } from 'react-router-dom'; // ← Added navigation
import { 
  trackDashboardView,
  trackCreditPurchaseView,
  trackUserEngagement,
  trackError 
} from '../../utils/analytics';

const Dashboard = () => {
  const { currentUser, userCredits, getUserCredits, logout } = useAuth(); // ← Added logout
  const navigate = useNavigate(); // ← Added navigate hook
  const { 
    getTransactionsByStatus
  } = useTransactions();

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false); // ← Added logout loading state
  
  // Track dashboard view on component mount
  useEffect(() => {
    if (currentUser) {
      trackDashboardView({
        user_id: currentUser.uid,
        credits_available: userCredits || 0,
        session_start: new Date().toISOString()
      });
    }
  }, [currentUser, userCredits]);

  // Calculate stats
  const completedCount = getTransactionsByStatus(TRANSACTION_STATUS.COMPLETED).length;
  const processingCount = getTransactionsByStatus(TRANSACTION_STATUS.PROCESSING).length;
  const errorCount = getTransactionsByStatus(TRANSACTION_STATUS.ERROR).length;

  // ← Added logout handler
  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      
      // Track user logout
      trackUserEngagement('logout', {
        user_id: currentUser?.uid,
        session_duration: 'session_end'
      });
      
      await logout();
      navigate('/'); // Redirect to home page
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Track logout error
      trackError({
        errorType: 'logout_error',
        errorMessage: error.message || 'Failed to logout',
        component: 'Dashboard'
      });
      
      alert('Failed to logout. Please try again.');
    } finally {
      setLogoutLoading(false);
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = async (paymentResponse) => {
    setPaymentLoading(true);
    try {
      // The payment data is embedded in the Paystack response metadata
      const paymentData = {
        userId: currentUser.uid,
        credits: paymentResponse.metadata?.credits || 0,
        amount: (paymentResponse.amount || 0) / 100, // Convert from kobo to rands
        packageId: paymentResponse.metadata?.packageId,
        email: currentUser.email
      };

      // Show immediate payment success
      alert(`Payment successful! Please wait while we add ${paymentData.credits} credits to your account. If this takes too long, simply refresh your browser page to see your updated credits.`);

      // Wait for webhook to process payment and add credits
      await waitForWebhookProcessing(paymentResponse, paymentData);
      
      // Refresh user credits after webhook processing
      await getUserCredits(currentUser.uid);
      
      // Show final success message
      alert(`✅ Success! ${paymentData.credits} credits have been added to your account.`);
      
    } catch (error) {
      console.error('Error processing payment:', error);
      // The error message from waitForWebhookProcessing is already user-friendly
      alert(error.message);
    } finally {
      setPaymentLoading(false);
      setShowPaymentModal(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="main-container">
      {/* UPDATED Header with Logout Button */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  PDF Statement Converter
                </h1>
                <p className="text-gray-600">
                  Convert your bank statements to accounting-ready CSV files
                </p>
              </div>
              
              {/* Updated right section with user info and logout */}
              <div className="flex items-center space-x-6">
                {/* Credits Display */}
                <div className="text-right">
                  <div className="text-sm text-gray-500">Credits Available</div>
                  <div className="text-2xl font-bold text-green-600" data-testid="credit-balance">
                    {userCredits || 0}
                  </div>
                </div>
                
                {/* Buy Credits Button */}
                <button 
                  onClick={() => {
                    trackCreditPurchaseView({
                      user_id: currentUser?.uid,
                      current_credits: userCredits || 0,
                      source: 'dashboard_header'
                    });
                    setShowPaymentModal(true);
                  }}
                  disabled={paymentLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                  data-testid="payment-modal-trigger"
                >
                  {paymentLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </>
                  ) : (
                    <>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Buy Credits
                    </>
                  )}
                </button>

                {/* User Info & Logout Section */}
                <div className="flex items-center space-x-4 pl-4 border-l border-gray-200">
                  {/* User Email (hidden on small screens) */}
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {currentUser?.displayName || 'User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {currentUser?.email}
                    </p>
                  </div>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogout}
                    disabled={logoutLoading}
                    className={`
                      flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                      ${logoutLoading 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    title="Logout"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">
                      {logoutLoading ? 'Signing out...' : 'Logout'}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner for New Users */}
        <RefreshBanner
          userCredits={userCredits}
          currentUser={currentUser}
        />

        {/* Low Credits Warning */}
        {(userCredits || 0) < 5 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-3" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">
                  Low Credits Warning
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  You have {userCredits || 0} credits remaining. Purchase more credits to continue converting statements.
                </p>
              </div>
              <button 
                onClick={() => {
                  trackCreditPurchaseView({
                    user_id: currentUser?.uid,
                    current_credits: userCredits || 0,
                    source: 'low_credits_warning'
                  });
                  setShowPaymentModal(true);
                }}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm font-medium"
              >
                Buy Now
              </button>
            </div>
          </div>
        )}


        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCardIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Available Credits</div>
                <div className="text-2xl font-bold text-gray-900">{userCredits || 0}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentArrowUpIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Total Processed</div>
                <div className="text-2xl font-bold text-gray-900">{completedCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ChartBarIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Processing</div>
                <div className="text-2xl font-bold text-gray-900">{processingCount}</div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Failed</div>
                <div className="text-2xl font-bold text-gray-900">{errorCount}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Credit Activity Link */}
        <div className="flex justify-end mb-8">
          <button
            onClick={() => navigate('/activity')}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-2 rounded-lg transition-colors"
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            View credit activity
          </button>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                Upload New Statement
              </h2>
              <div data-testid="upload-area">
                <FileUpload />
              </div>
            </div>
          </div>

          {/* Transaction History Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                Recent Conversions
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                CSV files are automatically deleted after 30 days for privacy protection
              </p>
              <div data-testid="transaction-history">
                <TransactionHistory limit={5} />
              </div>
            </div>
          </div>
        </div>

        {/* Credit Purchase Modal */}
        <CreditPurchaseModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={handlePaymentSuccess}
        />
      </div>
    </div>
  );
};

export default Dashboard;